<template>
	<GridRowLabel :label="$t('WEBAPP_PROMOTED')" class="dark">
		<!-- V1: Pre-roll video ad -->
		<PreRollVideoAd v-if="isV1" adUnit="EXP_Buybox_Video_V1">
			<template #fallback>
				<GridQTV
					v-if="catalogueItem"
					class="row-full-padding"
					:catalogueItem="catalogueItem"
					:providerShortName="providerShortName"
					:shortName="shortName"
					:title="title"
					:isFreeBuyBox="isFreeBuyBox"
					:additionalContexts="additionalContexts"
					:trackingContexts="trackingContexts"
					:seasonNumber="seasonNumber"
					:episodeNumber="episodeNumber"
					:displayPromotedTag="false"
				/>
			</template>
		</PreRollVideoAd>

		<!-- V2: Static banner ad with refresh, QTV fallback on failure -->
		<template v-else-if="isV2">
			<GridPreRollExpBannerAd v-show="v2AdFilled !== false" @slotReceived="v2AdFilled = $event" />
			<GridQTV
				v-if="v2AdFilled === false && catalogueItem"
				class="row-full-padding"
				:catalogueItem="catalogueItem"
				:providerShortName="providerShortName"
				:shortName="shortName"
				:title="title"
				:isFreeBuyBox="isFreeBuyBox"
				:additionalContexts="additionalContexts"
				:trackingContexts="trackingContexts"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
				:displayPromotedTag="false"
			/>
		</template>

		<!-- V3: Swiper with pre-roll (pos 1) + free trial (pos 2) -->
		<template v-else-if="isV3">
			<GridPromotionSwiper
				:slideDelay="8000"
				:items="[
					{ type: 'pre-roll', shortName: providerShortName },
					{ type: 'ft', shortName: providerShortName },
				]"
				:title="title"
				:offers="[]"
				:additionalContexts="additionalContexts"
			>
				<template #default="{ item, index }">
					<div v-if="item.type === 'pre-roll'" class="v3-video-wrapper">
						<PreRollVideoAd adUnit="EXP_Buybox_Swiper_Video_V3">
							<template #fallback>
								<GridQTV
									v-if="catalogueItem"
									:catalogueItem="catalogueItem"
									:title="title"
									:additionalContexts="additionalContexts"
									:swiperIndex="index"
									:displayPromotedTag="false"
									:hardcodedShortName="item.shortName"
									:providerShortName="providerShortName"
									:shortName="shortName"
									:isFreeBuyBox="isFreeBuyBox"
									:trackingContexts="trackingContexts"
									:seasonNumber="seasonNumber"
									:episodeNumber="episodeNumber"
								/>
							</template>
						</PreRollVideoAd>
					</div>
					<GridSwiperFreeTrial
						v-else-if="item.type === 'ft'"
						:catalogueItem="catalogueItem"
						:title="title"
						:additionalContexts="additionalContexts"
						:displayPromotedTag="false"
						:hardcodedShortName="item.shortName"
					/>
				</template>
			</GridPromotionSwiper>
		</template>
	</GridRowLabel>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useExperiment } from '@/libraries/experiment';
import GridRowLabel from '@/features/buybox/components/grid/GridRowLabel.vue';
import PreRollVideoAd from './components/PreRollVideoAd.vue';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { CatalogueItem } from '@/helpers/promotion-helper';

import GridPromotionSwiper from '@/components/experiments/GridSwiperExp/components/GridPromotionSwiper.vue';
const GridSwiperFreeTrial = defineAsyncComponent(
	() => import('@/components/experiments/GridSwiperExp/GridSwiperFreeTrial.vue')
);
const GridPreRollExpBannerAd = defineAsyncComponent(
	() => import('@/components/experiments/PreRollAndStaticBBAdsExp/components/GridPreRollExpBannerAd.vue')
);
const GridQTV = defineAsyncComponent(() => import('@/components/experiments/GridSwiperExp/GridQTV.vue'));

const v2AdFilled = ref<boolean | null>(null);

defineProps<{
	title: TitleDetail;
	catalogueItem: CatalogueItem | null;
	providerShortName?: string | null;
	shortName?: string | null;
	isFreeBuyBox?: boolean;
	additionalContexts?: SnowplowContext[];
	trackingContexts?: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
}>();

const { activeVariantWithControlGroup: variant } = useExperiment('EXP_PRE_ROLL_GRID');

const isV1 = computed(() => variant.value === 'variant_1');
const isV2 = computed(() => variant.value === 'variant_2');
const isV3 = computed(() => variant.value === 'variant_3');
</script>

<style lang="scss" scoped>
.row-full-padding {
	flex: 1;
	min-width: 0;
	padding: 16px;
}

.v3-video-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
}
</style>
